/**
 * Brand Login Component
 * Login page for brand partners to access their portal
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';
import '../assets/css/brand-portal.css';

function BrandLogin() {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading } = useBrand();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeactivated, setIsDeactivated] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/brand-portal/dashboard');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setIsDeactivated(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsDeactivated(false);
    setIsSubmitting(true);

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        navigate('/brand-portal/dashboard');
      } else {
        if (result.deactivated) {
          setIsDeactivated(true);
        }
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="brand-auth-page">
        <div className="brand-spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  return (
    <div className="brand-auth-page">
      <div className="brand-auth-container">
        <div className="brand-auth-card">
          <div className="brand-auth-header">
            <Link to="/">
              <img
                src={require('../assets/images/Y.png')}
                alt="Yemberzal"
                className="brand-auth-logo"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/default-image.avif';
                }}
              />
            </Link>
            <h1 className="brand-auth-title">Brand Portal</h1>
            <p className="brand-auth-subtitle">
              Sign in to manage your products and brand profile
            </p>
          </div>

          {error && !isDeactivated && (
            <div className="brand-alert brand-alert-error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          {isDeactivated && (
            <div className="brand-alert brand-alert-error" style={{ background: '#fef3c7', borderColor: '#f59e0b', color: '#92400e' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <div>
                <strong style={{ display: 'block', marginBottom: '4px' }}>Account Deactivated</strong>
                {error}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="brand-form-group">
              <label htmlFor="email" className="brand-form-label">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                className="brand-form-input"
                placeholder="your@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />
            </div>

            <div className="brand-form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label htmlFor="password" className="brand-form-label">Password</label>
                <Link
                  to="/brand-portal/forgot-password"
                  style={{
                    fontSize: 13,
                    color: '#666',
                    textDecoration: 'none',
                    marginBottom: 8,
                  }}
                >
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                id="password"
                name="password"
                className="brand-form-input"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              className="brand-btn brand-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="brand-spinner" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="brand-auth-divider">
            <span>New brand partner?</span>
          </div>

          <Link to="/partner" className="brand-btn brand-btn-secondary" style={{ textDecoration: 'none' }}>
            Apply for Partnership
          </Link>

          <div className="brand-auth-footer">
            <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>
              ← Back to Yemberzal
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BrandLogin;
